import { useState } from 'react';

import { LEAccordionItem } from '../../../../../components/LEAccordion';
import { useLEAccordionItemContext } from '../../../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import { useLEAccordionContext } from '../../../../../components/LEAccordion/useLEAccordion';
import { ItemStatusType } from '../../../../../components/LEAccordion/utils';
import VerifyNumberPanel from '../../../../../components/marketplace/phoneVerify/VerifyNumberPanel';
import { useMarketplaceExternalId } from '../../../../../services/marketplace/externalId';
import { useGetLeadByExternalId } from '../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import { normalizePhone } from '../../../../../utils/strings';
import EditPhoneNumberForm from './EditPhoneNumberForm';

const VerifyItemContent = ({ dealId, customerId }: { dealId: string; customerId: string }) => {
  const { updateState, index } = useLEAccordionItemContext();
  const { itemStates, updateItemState } = useLEAccordionContext();
  const externalId = useMarketplaceExternalId(dealId);
  const leadQuery = useGetLeadByExternalId({ externalId });

  const [isEditing, setIsEditing] = useState(false);

  const phoneNumber = normalizePhone(leadQuery.data?.phone ?? '');
  const verifiedNumber =
    leadQuery.data?.smsConsent?.status === 'verified' ? phoneNumber : undefined;

  const handlePhoneSaved = async () => {
    updateState({ status: 'none' });
    Object.values(itemStates).forEach((item) => {
      if (item.index > index) updateItemState(item.key, { status: 'none' });
    });
    await leadQuery.refetch();
    setIsEditing(false);
  };

  if (leadQuery.isLoading) return null;

  if (isEditing) {
    return (
      <EditPhoneNumberForm
        externalId={externalId}
        customerId={customerId}
        initialPhone={phoneNumber}
        onSaved={handlePhoneSaved}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  return (
    <VerifyNumberPanel
      phoneNumber={phoneNumber}
      dealId={dealId}
      verifiedNumber={verifiedNumber}
      onEditNumber={() => setIsEditing(true)}
      onVerified={() => leadQuery.refetch()}
    />
  );
};

type MarketplacePrelistVerifyItemProps = {
  dealId: string;
  customerId: string;
  initialStatus?: ItemStatusType;
};

const MarketplacePrelistVerifyItem = ({
  dealId,
  customerId,
  initialStatus,
}: MarketplacePrelistVerifyItemProps) => {
  return (
    <LEAccordionItem
      accordionKey="prelistVerifyNumber"
      title="VERIFY YOUR NUMBER"
      subtitle="This is how we'll text you the moment an offer comes in."
      initialStatus={initialStatus}
    >
      <VerifyItemContent dealId={dealId} customerId={customerId} />
    </LEAccordionItem>
  );
};

export default MarketplacePrelistVerifyItem;
